import React, { useState } from 'react';
import { aiService } from '../../../services/aiService';
import { useAiRequest } from './useAiRequest';
import { AiBox, AiSection } from './AiBox';
import { AiItems, itemsToText } from './AiItems';
import { Spinner } from '../../ui/Spinner';
import { Alert } from '../../ui/Alert';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const PERIODS = [
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 3 months' },
  { days: 365, label: 'Last year' }
];

export const ReflectionPanel = ({ moments, onOpenMoment }) => {
  const ai = useAiRequest();
  const [days, setDays] = useState(90);
  const [open, setOpen] = useState(false);
  const r = ai.data?.result;

  const start = () => {
    const end = new Date();
    const from = new Date(end.getTime() - days * 86400000);
    ai.run(() => aiService.reflect(from, end));
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-6 flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100"
      >
        <SafeIcon icon={FiIcons.FiStar} /> Reflect on my Moments
      </button>
    );
  }

  return (
    <div className="mb-6 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
        >
          {PERIODS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
        </select>
        <button
          type="button"
          disabled={ai.loading || moments.length < 3}
          onClick={start}
          className="rounded-full bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
        >
          Reflect
        </button>
        <button type="button" onClick={() => { ai.reset(); setOpen(false); }} className="text-xs font-semibold text-slate-500 hover:underline">
          Close
        </button>
        {moments.length < 3 && <p className="text-xs text-slate-500">Add a few more Moments first.</p>}
      </div>
      {ai.loading && <p className="flex items-center gap-2 text-xs text-slate-500"><Spinner size="sm" /> Reading your Moments…</p>}
      {ai.error && <Alert variant="error" title="AI is unavailable" message={ai.error} />}
      {ai.data && (
        <AiBox
          title="Reflection"
          meta={ai.data.meta}
          copyText={ai.data.insufficient ? '' : `${r.summary}\n\n${itemsToText('Themes', r.themes)}${itemsToText('Notable', r.notable)}${r.reflection_question || ''}`}
          onDismiss={ai.reset}
        >
          {ai.data.insufficient ? <p>{ai.data.message}</p> : (
            <>
              {r.summary && <AiSection label="Overview"><p>{r.summary}</p></AiSection>}
              <AiItems label="Recurring themes" items={r.themes} moments={moments} onOpen={onOpenMoment} />
              <AiItems label="Encounters that seem to matter" items={r.notable} moments={moments} onOpen={onOpenMoment} />
              {r.reflection_question && <AiSection label="Something to consider"><p className="italic">{r.reflection_question}</p></AiSection>}
              <p className="text-[11px] text-violet-700/70">
                Based on {ai.data.meta?.moments_considered} Moments. People's names were not sent.
              </p>
            </>
          )}
        </AiBox>
      )}
    </div>
  );
};